import React, { useEffect, useState } from 'react';
import {
    Button,
    FlatList,
    Pressable,
    ScrollView,
    VStack,
    Box,
    Modal
} from 'native-base';
import { SBDropdown } from '~/shared/SBDropdown';
import { SBRangeSlider } from '~/shared/SBRangeSlider';
import { Caption } from '~/shared/Typography';
import { useShopStyles } from '~/shared/styles';
import {
    MenuFilter,
    StrainType,
    Effects,
    useDutchieStore,
    useI18n
} from '@springbig/shared';

export type FilterModalProp = {
    showFilters: boolean;
    setShowFilters: () => void;
    filters: MenuFilter | null | undefined;
    setFilters: (filters: MenuFilter) => Promise<void>;
};

export const FilterModal = ({ showFilters, setShowFilters, filters, setFilters }: FilterModalProp) => {

    const { section_background, text, pressable_text } = useShopStyles();
    const [selectedStrainType, setSelectedStrainType] = useState<StrainType | undefined>(undefined);
    const [selectedEffects, setSelectedEffects] = useState<Array<Effects>>([]);
    const [minValue, setMinValue] = useState(filters?.minPrice ?? 0);
    const [maxValue, setMaxValue] = useState(filters?.maxPrice ?? 1000);
    const { t } = useI18n();

    useEffect(() => {

        if (filters) {
            if (filters.strainType) {
                setSelectedStrainType(filters.strainType);
            }

            if (filters.effects) {
                setSelectedEffects(filters.effects);
            }
        }
    }, []);

    const onSelectFilter = (): void => {

        setShowFilters();
        const newFilters : MenuFilter = {
            strainType: selectedStrainType,
            effects: selectedEffects,
            minPrice: minValue,
            maxPrice: maxValue,
        };

        setFilters(newFilters);
    };

    const onSelectEffect = (effects: Effects): void => {

        if (selectedEffects.includes(effects)) {
            setSelectedEffects(selectedEffects.filter((e) => e !== effects));
        }
        else {
            setSelectedEffects([effects, ...selectedEffects]);
        }
    };

    const onSelectStrainType = (strainType: StrainType): void => {

        if (strainType === selectedStrainType) {
            setSelectedStrainType(undefined);
        }
        else {
            setSelectedStrainType(strainType);
        }
    };

    return (
        <Modal isOpen={showFilters}>
            <Modal.Content>
                <Modal.Header>{ t('shop.apply_filters') }</Modal.Header>
                <Modal.CloseButton
                    bg={section_background}
                    _icon={{
                        color: pressable_text,
                    }}
                    onPress={setShowFilters}
                />
                <Modal.Body>
                    <ScrollView>
                        <VStack space={4}>
                            <SBDropdown  title="strain type" defaultShowChildren={true}>
                                <FlatList
                                    bg={section_background}
                                    data={[StrainType.Indica, StrainType.Sativa, StrainType.Hybrid]}
                                    renderItem={({ item, index }: {item: StrainType, index: number}) =>

                                        <Button
                                            rounded={4}
                                            mx={index % 2 === 0 ? 0 : 2}
                                            p={1}
                                            alignItems={'center'}
                                            key={index}
                                            onPress={() => onSelectStrainType(item)}
                                        >
                                            <Caption color={selectedStrainType === item ? pressable_text : text}>
                                                { item.toLowerCase() }
                                            </Caption>
                                        </Button>
                                    }
                                />
                            </SBDropdown>

                            <SBDropdown title="effects">
                                <FlatList
                                    bg={section_background}
                                    numColumns={3}
                                    data={Object.values(Effects)}
                                    renderItem={({ item }: { item: Effects }) =>

                                        <Button
                                            rounded={'md'}
                                            w={'1/3'}
                                            p={1}
                                            alignItems={'center'}
                                            onPress={() => onSelectEffect(item)}
                                        >
                                            <Caption color={selectedEffects.includes(item) ? pressable_text : text}>
                                                { item.toLowerCase().replace('_', ' ') }
                                            </Caption>
                                        </Button>
                                    }
                                />
                            </SBDropdown>
                            <SBDropdown
                                title="price"
                                defaultShowChildren={true}
                            >
                                <Box bg={section_background} p={1}>
                                    <SBRangeSlider
                                        minValue={minValue}
                                        maxValue={maxValue}
                                        lowerBound={0}
                                        upperBound={1000}
                                        onChangeMinValue={(value) => setMinValue(value)}
                                        onChangeMaxValue={(value) => setMaxValue(value)}
                                    />
                                </Box>
                            </SBDropdown>
                        </VStack>
                    </ScrollView>
                </Modal.Body>
                <Modal.Footer>
                    <Pressable
                        bg={section_background}
                        py={1}
                        px={2}
                        borderRadius={4}
                        onPress={onSelectFilter}
                    >
                        <Caption>{ t('shop.apply') }</Caption>
                    </Pressable>
                </Modal.Footer>
            </Modal.Content>
        </Modal>
    );
};
